/* <silo-v2-header> — the new design's phone header controller (home v2, PDP v2 …).
   Rendered once per page by snippets/silo-v2-header.liquid at the top of the first section.

   The header never turns white and never hides on its own: it is one glass bar whose tint adapts to what sits
   under it (the CSS states live in assets/app-tailwind.css, keyed on body[data-template-suffix='v2']):
     body.hv-scrolled      — the page has moved past the header stack (announcement + bar): the tint deepens
     body.hv-glass-light   — a light surface is under the bar: off-white glass + green ink
     #header-container.hv-hidden / --hv-shift — Instagram-style hide on scroll down, show after SHOW_AFTER px up
   No scroll listener anywhere: an IntersectionObserver column of 1px sentinels resolves position + direction,
   a ResizeObserver keeps the column as tall as the document, a MutationObserver re-opens the bar when the menu opens.
   Attributes: data-header-scroll="hide|always", data-header-show-after="100" (px). Phones (< 768px) only — tablets/desktop keep the theme's floating pill. */
(function () {
  'use strict';
  if (customElements.get('silo-v2-header')) return;

  const STEP = 24; // base sentinel spacing: direction resolves within 24px; distances are counted in STEP multiples
  const MAX_NODES = 400; // the column never holds more sentinels than this: taller pages use a coarser spacing

  customElements.define('silo-v2-header', class SiloV2Header extends HTMLElement {
    connectedCallback() {
      const header = document.getElementById('header-container');
      const phone = window.matchMedia('(max-width: 767px)');
      if (!phone.matches || !header) return; // tablets/desktop keep the theme's own header behaviour
      this._ac = new AbortController();
      const { signal } = this._ac;
      const body = document.body;

      // ── scrolled tint ──
      // body.hv-scrolled only once the page has moved past the whole header stack — exactly where the bar hides on the
      // way down — so scrolling away from the top never shows a glass flash: the bar stays fully transparent, then
      // slides away; it comes back as glass on the way up and clears again inside the first stack-height.

      // ── which surface is under the bar? ──
      // A hit-test at the bar's RESTING centre (announcement height + half the bar — independent of whether the bar
      // is tucked away), walked up to the first painted background; its luminance picks light/dark glass.
      // hv-header-light / hv-header-dark on any ancestor override it. Throttled to 4 probes/s (trailing: a call inside
      // the window is deferred, never dropped), so steady scrolling costs ~1 hit-test per 250 ms.
      const lum = (bg) => {
        const m = bg && bg.match(/[\d.]+/g);
        if (!m || m.length < 3) return null;
        if (m.length > 3 && +m[3] < 0.5) return null;
        const [r, g, b] = m.map(Number);
        return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
      };
      let stackH = 120, annH = 48, lastProbe = 0;
      const measure = () => {
        const ann = header.querySelector('.homePgaeAnnou');
        annH = ann && ann.getClientRects().length ? ann.offsetHeight : 0;
        stackH = annH + header.offsetHeight; // the bar sits right under the announcement
      };
      const probe = () => {
        const wait = 250 - (performance.now() - lastProbe);
        if (wait > 0) { clearTimeout(this._probeT); this._probeT = setTimeout(probe, wait); return; }
        lastProbe = performance.now();
        const under = document.elementsFromPoint(window.innerWidth / 2, annH + (stackH - annH) / 2)
          .find((el) => !header.contains(el) && getComputedStyle(el).position !== 'fixed');
        let light = true; let el = under;
        while (el && el !== document.documentElement) {
          const force = el.classList && (el.classList.contains('hv-header-light') ? 'light' : el.classList.contains('hv-header-dark') ? 'dark' : null);
          if (force) { light = force === 'light'; break; }
          const l = lum(getComputedStyle(el).backgroundColor);
          if (l !== null) { light = l > 0.55; break; }
          el = el.parentElement;
        }
        body.classList.toggle('hv-glass-light', light);
      };
      measure(); probe();
      let resizeT = 0;
      window.addEventListener('resize', () => { clearTimeout(resizeT); resizeT = setTimeout(() => { measure(); probe(); }, 150); }, { signal, passive: true });

      // ── Instagram hide-show ──
      // Direction comes from an IntersectionObserver watching a column of 1px sentinels (one every STEP px) through a
      // thin band at the top of the viewport: the sentinel in the band tells the position, its change the direction.
      // The compositor drives the observer, so there is no per-frame JavaScript; the bar only toggles open/closed and
      // the 200 ms CSS transition does every move. Down → the stack slides away at once. Up → it stays away until the
      // finger has travelled SHOW_AFTER px. Inside the first stack-height of the page it is always open; so is it while
      // the menu is open.
      const hideOnScroll = this.dataset.headerScroll !== 'always';
      const SHOW_AFTER = Math.max(16, parseInt(this.dataset.headerShowAfter, 10) || 100);
      let lastY = 0;
      const setOpen = (open) => {
        if (this._open === open) return;
        this._open = open;
        header.style.setProperty('--hv-shift', open ? '0px' : (-stackH) + 'px');
        header.classList.toggle('hv-hidden', !open);
        // the glass tint belongs to a bar that is SHOWN below the stack: never while it slides away from the top
        body.classList.toggle('hv-scrolled', open && lastY >= stackH);
      };
      this._open = true; header.classList.add('hv-snap'); // the transition is always on: every move is a toggle
      // The column is built once and only ever EXTENDED: a body resize appends the missing sentinels (one pass per
      // animation frame) and nothing is torn down, so a growing page costs a few nodes, not a rebuild of thousands.
      // Its spacing scales with the page so the column stays ≤ MAX_NODES (a 66k px PDP → 168px steps, ~400 nodes);
      // a page that outgrows the current spacing is rebuilt once at the coarser one. The observer is only recreated
      // when the viewport height changes (its band is sized from it).
      let col = null, dirIo = null, lastIdx = null, up = 0, step = STEP, built = 0, ioVH = 0; const inBand = new Set();
      const stepFor = (h) => STEP * Math.max(1, Math.ceil(h / (MAX_NODES * STEP)));
      const observeAll = () => {
        if (dirIo) dirIo.disconnect(); inBand.clear(); lastIdx = null; ioVH = window.innerHeight;
        dirIo = new IntersectionObserver((entries) => {
          // position = the lowest-indexed sentinel inside the band. A batch only lists sentinels that CHANGED, so the
          // band's membership is kept across batches; a sentinel leaving above the band = scrolled down.
          let leftAbove = false, leftBelow = false;
          for (const en of entries) { const i = +en.target.dataset.i; if (en.isIntersecting) inBand.add(i); else { inBand.delete(i); if (en.boundingClientRect.top < 0) leftAbove = true; else leftBelow = true; } }
          let idx = null; inBand.forEach((i) => { if (idx === null || i < idx) idx = i; });
          let delta = 0, y = null;
          if (idx !== null) {
            // a sentinel sits in the band: exact position. The travel is measured from the last EXACT position (the
            // anchor), so a fling that skipped sentinels still yields its full distance on landing.
            y = idx * step; delta = lastIdx === null ? 0 : (idx - lastIdx) * step; lastIdx = idx;
          } else {
            // a fast fling skipped the band: only the leave side is known. Leaving above = scrolling down → close now;
            // leaving below = scrolling up → distance unknown, wait for the landing (no guessed steps).
            if (!leftAbove || leftBelow) return;
            delta = step;
          }
          if (y !== null) { lastY = y; if (this._open) body.classList.toggle('hv-scrolled', y >= stackH); }
          if (!hideOnScroll) { probe(); return; }
          if (body.dataset.menuOpen === 'true' || (y !== null && y < stackH)) { up = 0; setOpen(true); probe(); return; }
          if (delta > 0) { up = 0; setOpen(false); }
          else if (delta < 0) { up += -delta; if (up + step / 2 >= SHOW_AFTER) { up = 0; setOpen(true); probe(); } } // rounded to the nearest step
        }, { rootMargin: '0px 0px -' + Math.max(0, ioVH - 2 * step) + 'px 0px', threshold: 0 });
        col.querySelectorAll('div').forEach((d) => dirIo.observe(d));
        this._dirIo = dirIo;
      };
      const syncColumn = () => {
        // measured from <body>'s own content, never scrollHeight: that includes this column, so it could only ever grow
        // and a page that got shorter (lazy sections settling) kept a blank strip under the footer
        const h = Math.max(Math.ceil(body.getBoundingClientRect().bottom + window.scrollY), window.innerHeight);
        const want = stepFor(h);
        if (!col || want !== step) {
          // first build, or the page outgrew the spacing: start over at the coarser step
          if (col) col.remove();
          step = want; built = 0;
          col = document.createElement('div');
          col.setAttribute('data-hv-sentinels', ''); col.setAttribute('aria-hidden', 'true');
          col.style.cssText = 'position:absolute;top:0;left:0;width:0;height:' + h + 'px;overflow:hidden;pointer-events:none;visibility:hidden;';
          body.appendChild(col); this._col = col;
          if (dirIo) { dirIo.disconnect(); dirIo = null; }
        }
        const n = Math.ceil(h / step) + 1;
        if (n > built) {
          const frag = document.createDocumentFragment();
          for (let i = built; i < n; i++) { const d = document.createElement('div'); d.style.cssText = 'position:absolute;left:0;width:1px;height:1px;top:' + (i * step) + 'px;'; d.dataset.i = i; frag.appendChild(d); }
          col.appendChild(frag);
          if (dirIo && ioVH === window.innerHeight) { for (let i = built; i < n; i++) dirIo.observe(col.children[i]); } // extend: keep the state
          built = n;
        }
        col.style.height = h + 'px'; // follows the page both ways; sentinels past it are clipped (overflow) and never scroll
        if (!dirIo || ioVH !== window.innerHeight) observeAll();
      };
      syncColumn();
      // the page grows as lazy sections load: keep the column as tall as the document (ResizeObserver, no polling),
      // coalesced to one pass per frame
      if ('ResizeObserver' in window) { this._ro = new ResizeObserver(() => { if (this._colRaf) return; this._colRaf = requestAnimationFrame(() => { this._colRaf = 0; syncColumn(); }); }); this._ro.observe(body); }
      // opening the menu while the header is tucked away must bring it back at once (the theme flags it on <body>)
      if ('MutationObserver' in window) {
        this._menuMo = new MutationObserver(() => { if (body.dataset.menuOpen === 'true') setOpen(true); });
        this._menuMo.observe(body, { attributes: true, attributeFilter: ['data-menu-open'] });
      }
    }

    disconnectedCallback() {
      if (this._ac) this._ac.abort();
      document.body.classList.remove('hv-scrolled', 'hv-glass-light');
      const hdr = document.getElementById('header-container');
      if (hdr) { hdr.classList.remove('hv-snap', 'hv-hidden'); hdr.style.removeProperty('--hv-shift'); }
      if (this._dirIo) { this._dirIo.disconnect(); this._dirIo = null; }
      if (this._ro) { this._ro.disconnect(); this._ro = null; }
      if (this._colRaf) { cancelAnimationFrame(this._colRaf); this._colRaf = 0; }
      clearTimeout(this._probeT);
      if (this._col) { this._col.remove(); this._col = null; }
      if (this._menuMo) { this._menuMo.disconnect(); this._menuMo = null; }
    }
  });
})();
